import { ErrorMessage } from '@hookform/error-message';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import type { VoucherModel } from 'autogen/swagger/all';
import { useRemoveVoucherMutation, useUpdateVoucherCodeMutation } from 'autogen/swagger/all/hooks';
import {
    AddVoucher,
    Code,
    InputPlaceholder,
    Notice,
    Redeem
} from 'autogen/translation-keys/trans-website-basket-voucher';
import { Button, ButtonWithIcon, ButtonWithSpinner } from 'components/Button';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import HighlightedText from 'components/generic/HighlightedText';
import { Typography } from 'components/generic/typography';
import TextInput from 'components/shared/inputs/text';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DeleteAllButton from '../BasketItem/components/Actions/components/DeleteAllButton';
import { updateBasket } from '../shared/basket';

const StyledFlexRow = styled(Flex.Row)`
    width: 100%;
`;

const StyledTextInputWithValidation = styled(TextInput)`
    width: 100%;
`;

export default function Voucher({ model: { hasVoucher, showVoucherInfo, code } }: { model: VoucherModel }) {
    const { t } = useTranslation();
    const {
        register,
        setError,
        getFieldState,
        clearErrors,
        getValues,
        formState: { errors }
    } = useForm();

    const voucherFieldName = 'voucherCode';
    const { mutateAsync: updateVoucher, data } = useUpdateVoucherCodeMutation({
        onSuccess: ({ validationErrors }) => {
            if (!validationErrors.length) {
                updateBasket();
                clearErrors([voucherFieldName]);
                return;
            }

            validationErrors.forEach((error, index) => {
                setError(`${voucherFieldName}${index}`, { message: error.errorMessage });
            });
        }
    });

    const { mutateAsync: removeVoucher } = useRemoveVoucherMutation({
        onSuccess: ({ success }) => {
            if (success) {
                updateBasket();
            }
        }
    });

    const [isActive, setIsActive] = useState(false);
    const [showSecretDeleteBtn, setShowSecretDeleteBtn] = useState(false);

    if (!hasVoucher)
        return (
            <Flex.Column alignItems="start">
                {!isActive && (
                    <Button variant="primaryText" onClick={() => setIsActive((prev) => !prev)}>
                        {t(AddVoucher)}
                    </Button>
                )}

                {isActive && (
                    <StyledFlexRow gap={Spacing8}>
                        <StyledTextInputWithValidation
                            placeholder={t(InputPlaceholder)}
                            isInvalid={getFieldState(voucherFieldName).invalid}
                            {...register(voucherFieldName)}
                        />
                        <ButtonWithSpinner
                            onClick={async () => {
                                const voucherCode = getValues(voucherFieldName);
                                if (!voucherCode) return;
                                if (voucherCode === 'secretDeleteButton') return setShowSecretDeleteBtn(true);

                                await updateVoucher({ params: { voucherCode } });
                            }}
                        >
                            {t(Redeem)}
                        </ButtonWithSpinner>
                    </StyledFlexRow>
                )}

                {data?.validationErrors.map((error, index) => (
                    <ErrorMessage
                        key={index}
                        name={`${voucherFieldName}${index}`}
                        errors={errors}
                        render={({ message }) => message}
                    />
                ))}
                {showSecretDeleteBtn && <DeleteAllButton />}
            </Flex.Column>
        );

    return (
        <Flex.Column alignItems="start">
            <Typography.Body as="span" size="M" strong>
                {t(Code)}
            </Typography.Body>

            <HighlightedText>
                <StyledFlexRow justifyContent="space-between" alignItems="center" gap={Spacing8}>
                    <Typography.Body as="span">{code}</Typography.Body>
                    <ButtonWithIcon
                        size="sm"
                        variant="ghostOnLight"
                        icon={<Icon type="Close" size={16} />}
                        onClick={() => removeVoucher({})}
                    />
                </StyledFlexRow>
            </HighlightedText>

            {showVoucherInfo && <Typography.Body as="span">{t(Notice)}</Typography.Body>}
        </Flex.Column>
    );
}
